<template>
    <div
      id="emailPreview"
      ref="emailPreview"
      style="height: 100%; width: 100%; border: none"
    ></div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import firebase from 'firebase/compat/app'
import { User } from '../../../models'
import { Utils } from '../../../util/utils'
import { useEmitter } from '../../composition/useEmitter'
import { contentAiPermission } from '../../../util/email_builder_helper'
import { loadRemote } from '@module-federation/runtime'
import { currencyCode } from '@/util/currency_helper'
import { EmailBuilderServices } from '../../../services'
import { isSupportUser, getCustomObjectLabels } from './utils/helper'

let emailPreviewModalComponent: any = {}

export default defineComponent({
  name: 'EmailBuilderPage',
   emits: ['model:close'],
  props: {
    location: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    routePath: {
      type: String,
      required: false,
    },
    customValues:{
      type:Array,
      required:false
    },
    subSourceId:{
      type:String,
      required:false
    },
    sourceId:{
      type:String,
      required:false
    },
    source: {
      type:String,
      required:false
    },
    skipCheck:{
      type:Boolean,
      required:false
  }
  },
  data() {
    return {
      handshake: {},
      emitter: undefined,
    }
  },
  beforeUnmount() {
   if(emailPreviewModalComponent?.unmount){
      emailPreviewModalComponent?.unmount()
      emailPreviewModalComponent = null
   }
    Utils.toggleCustomJsVisibility('inline')
    this.emitter.off('email_preview-exit-email-builder')
    this.emitter.off('email_preview-close-builder-modal')
    this.emitter.off('email_preview-content-ai-variations-generate')
    this.emitter.off('email_preview-content-ai-variations-show')
    this.emitter.off('email_preview-image-ai-generate')
    this.emitter.off('email_preview-image-ai-variations-show')
    this.emitter.off('email_preview-content-ai-quick-action')
    this.emitter.off('spm-ts')
    this.emitter.off('content-ai-generated-content')
    this.emitter.off('content-ai-generated-image')
    this.emitter.off('quick-action-generated-session')
    this.emitter.off('email_preview-route-to-brand-boards')
    this.emitter.off('email-preview-create-countdown-timer')
    this.emitter.off('email_preview-copy-to-clipboard')
    const workflowBuilderIframe = document.querySelector('iframe[name="workflow-builder"]')
    if(workflowBuilderIframe) workflowBuilderIframe.style.display = 'inline'
    this.emitter.off('mediaSelected')
  },
  mounted() {
    this.emitter = useEmitter()

    this.loadMicroApp()
    this.emitter.on('content-ai-generated-content', (id: string) => {
        this.emitter.emit('email_preview-content-ai-generated-content', id)
    })
    this.emitter.on('content-ai-generated-image', (id: string) => {
        this.emitter.emit('email_preview-content-ai-generated-image', id)
    })

    this.emitter.on('quick-action-generated-session', (payload: any) => {
        this.emitter.emit('email_preview-quick-action-generated-session', payload)
    })
    this.emitter.on('email_preview-route-to-brand-boards', () => {
      const routeData = this.$router.resolve({ name: 'brand-boards' })
      window.open(routeData.href, '_blank');
    })
    this.emitter.on('email-preview-create-countdown-timer', () => {
      const routeData = this.$router.resolve({ name: 'countdown-timer-list-v2' })
      window.open(routeData.href, '_blank');
    })
    this.emitter.on('mediaSelected', (file: any) => {
      if (file) {
        this.emitter.emit('email-preview-media-selected', file)
      }
      this.emitter.emit('closeMediaModal')
    })
    this.emitter.on('email_preview-copy-to-clipboard', (text: string) => {
      navigator.clipboard.writeText(text)
    })
    Utils.toggleCustomJsVisibility('none')
    //Hide the workflow iframe to fix the drag and drop issue for workflow
    const workflowBuilderIframe = document.querySelector('iframe[name="workflow-builder"]')
    if(workflowBuilderIframe) workflowBuilderIframe.style.display = 'none'
  },
  computed: {
    userValue() {
      return new User(this.user)
    },
    isNewBuilder(){
      return false
      // return this.$store.getters['featureFlags/getForLocation']('emailBuilder.flowRevamp')
    },
    isAutoSaveEnabled() {
      return this.$store.getters['featureFlags/getForLocation']('emailBuilder.autosave')
    },
    isSmartSendEnabled() {
      return this.$store.getters['featureFlags/getForLocation']('emailBuilder.smartSend')
    },
    isEmailAiEnabled() {
      return this.$store.getters['featureFlags/getForLocation']('emailBuilder.emailAi')
    },
    customObjectLabels() {
      return getCustomObjectLabels(this.$store)
    },
    isCustomColorEnabled() {
      return this.$store.getters['featureFlags/getForLocation']('emailBuilder.customColor')
    },
    isPreferenceManagementEnabled() {
      return this.$store.getters['featureFlags/getForLocation']('conversations.prefMgmtLabs')
    }
  },
  methods: {
    async getToken() {
      return await firebase.auth().currentUser.getIdToken()
    },
    async loadMicroApp() {
      this.emitter.on('email_preview-exit-email-builder', (data: any) => {
        let response = false
        if (data.unsavedChanges) {
          response = !window.confirm(data.message)
        }
        if (!response) {
          if (data.name) {
            const routeInfo = { name: data.name } as any
            if (data.query) {
              routeInfo.query = data.query
            }
            this.$router.push(routeInfo)
          } else this.$router.push(data.router)
        }
      })
      this.emitter.on('email_preview-close-builder-modal', (data: { 
        clonedTemplateId: string,
        isSubjectChanged: boolean,
        isNewTemplate: boolean 
      }) => {
        this.$emit('model:close', data)
      })

      this.emitter.on(
        'email_preview-content-ai-variations-generate',
        ({ source = 'email_builder', type }) => {
          this.emitter.emit('content-ai-variations-generate', {
            source,
            type,
          })
        }
      )

      this.emitter.on('email_preview-content-ai-variations-show', (id: string) => {
        this.emitter.emit('content-ai-variations-show', {
          source: 'email_builder',
          id,
        })
      })

      this.emitter.on(
        'email_preview-image-ai-generate',
        () => {
          this.emitter.emit('image-ai-generate', {
            source:'email_builder',
          })
        }
      )

      this.emitter.on('email_preview-image-ai-variations-show', (id: string) => {
        this.emitter.emit('image-ai-variations-show', {
          source: 'email_builder',
          id,
        })
      })

      this.emitter.on('email_preview-content-ai-quick-action', (payload:any) => {
        this.emitter.emit('content-ai-quick-action', {
          selectedText:payload.text,
          contextualType:payload.type,
          type:'body',
          source: 'email_builder',
        })
      })

      this.emitter.on('spm-ts', (data: any) => {
        this.$router.push(data.router)
      })

      const routerName = this.$route.name || this.routePath
      if (!routerName) return
      const routePath = this.routePath || this.$route.path

      const storageLocale = localStorage.getItem("locale")

      let locationId, emailId, isModalDialog = false, cloneTemplate = false
      const country = this.$store?.state?.locations?.currentLocation?.country || 'US'
      const currency = country ? currencyCode[country] : 'USD'

      if(routePath) {
        const match = routePath.match(/\/location\/([^\/]+)\/emails\/create\/([^\/]+)\/builder\?isModalDialog=(true|false|undefined)\&cloneTemplate=(true|false|undefined)/)
        if (match) {
          locationId = match[1]
          emailId = match[2]
          isModalDialog = match[3] === 'true'
          cloneTemplate = match[4] === 'true'
        } else {
          locationId = this.$route.params.location_id
          emailId = this.$route.params.template_id
        }
      }

      const user = await User.getById(this.user.id)
      const auth = this.$store.getters['auth/getAuth']
      const { apiKey } = auth
      if (emailPreviewModalComponent?.unmount) emailPreviewModalComponent?.unmount()

      try{
        const [createApp, App] = await Promise.all([
            loadRemote('emailPreviewApp/vue3').then((m: any) => m.createApp),
            loadRemote('emailPreviewApp/App').then((m: any) => m.default),
        ])
        if (this.sourceId && this.subSourceId && this.source === 'workflow' && !this.skipCheck) {
          let result
          const payload = {
            locationId,
            workflowId: this.sourceId,
            actionId: this.subSourceId,
            templateId: emailId,
          }
          try {
            const { data } = await EmailBuilderServices.getWorkflowDetails(payload)
            result = data
          } catch (error) {
            console.error(error)
          }

          if (
          !result ||
          result?.campaign?.documentId === null ||
          result?.campaign?.documentId !== emailId
        ) {
          try {
            const { data } = await EmailBuilderServices.updateWorkflowDetails(payload)
            result = data
            // Only update emailId if updateWorkflowDetails was successful
            if (result?.campaign?.documentId) {
              emailId = result.campaign.documentId
            }
          } catch (error) {
            console.error('Error updating workflow details:', error)
            // Don't update emailId when update fails - keep using the original emailId
          }
        } else if (result?.campaign?.documentId) {
          // Use the documentId from getWorkflowDetails if it matches the current emailId
          emailId = result.campaign.documentId
        }
        }
        let enableContentAI = await contentAiPermission(
          this.location.id,
          this.location.company_id,
          this.user.permissions
        )
        if(this.$route.name === 'saas-builder-labs') enableContentAI = false // disable content ai if it is called from agency

        emailPreviewModalComponent = createApp(App, {
          route: this.$route,
          router: this.$router,
          emitter: this.emitter,
          user,
          model: {
            userId: this.user.id,
            email: this.user.email,
            apiKey,
            userName: `${this.user.first_name} ${this.user.last_name}`,
            timeZone: this.location.timezone,
            tokenId: await this.getToken(),
            userPermissions: {
              ...this.user.permissions,
              enableContentAI,
            },
            enableAssignToData: this.user && this.user.role === 'user' && this.user.permissions.assigned_data_only ,
            companyId: this.location.company_id,
            isNewBuilder: this.isNewBuilder,
            customValues: this.customValues,
            locale: storageLocale || 'en',
            locationId,
            emailId,
            isModalDialog,
            cloneTemplate,
            parentBaseUrl: window.location.origin,
            country,
            currency,
            useInternalTemplate: this.source ? true : false,
            isAutoSaveEnabled: this.isAutoSaveEnabled,
            isSmartSendEnabled: this.isSmartSendEnabled,
            isEmailAiEnabled: this.isEmailAiEnabled,
            isSupportUser: isSupportUser(this.$store.state?.auth?.user?.jwt),
            customObjectLabels: this.customObjectLabels,
            isCustomColorEnabled: this.isCustomColorEnabled,
            isPreferenceManagementEnabled: this.isPreferenceManagementEnabled,
            source : this.source,
          },
        });
        emailPreviewModalComponent.mount(`#emailPreview`);
      }
      catch (error) {
        console.error('Error mounting email preview app', error)
      }
    },
  },
})
</script>
<style>
#emailBuilder iframe {
  width: 100%;
  height: 100%;
  border: none;
  font-family: inherit;
  -webkit-font-smoothing: inherit;
}
.email-builder {
  width: 100%;
  background: #ffffff;
}
</style>
